import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageSeo } from "../components/seo/PageSeo";
import { getContentByLocale, getMediaByLocale } from "../content";
import type { MediaItem, MediaType } from "../content";
import { useI18n } from "../i18n";

const validTypes: MediaType[] = ["fotos", "videos"];
const ITEMS_PER_PAGE = 6;
const MEDIA_CACHE_TTL_MS = 5 * 60 * 1000;

type PaginatedMediaResponse = {
  items: Array<any>;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

type CachedMediaResponse = {
  payload: PaginatedMediaResponse;
  cachedAt: number;
};

const mediaPageCache = new Map<string, CachedMediaResponse>();
const mediaPageInFlight = new Map<string, Promise<PaginatedMediaResponse | null>>();

function normalizeMediaPayload(payload: PaginatedMediaResponse | any[]): PaginatedMediaResponse {
  const payloadItems = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
      ? payload.items
      : [];

  return {
    items: payloadItems,
    page: Array.isArray(payload) ? 1 : Number(payload?.page ?? 1),
    limit: Array.isArray(payload) ? ITEMS_PER_PAGE : Number(payload?.limit ?? ITEMS_PER_PAGE),
    total: Array.isArray(payload) ? payloadItems.length : Number(payload?.total ?? payloadItems.length),
    totalPages: Array.isArray(payload)
      ? 1
      : Number(payload?.totalPages ?? (payloadItems.length > 0 ? 1 : 0)),
  };
}

function getYouTubeEmbedUrl(href: string): string {
  try {
    const parsed = new URL(href);
    if (parsed.hostname.includes("youtu.be")) {
      const videoId = parsed.pathname.replace("/", "");
      return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
    }

    if (parsed.hostname.includes("youtube.com")) {
      const videoId = parsed.searchParams.get("v");
      return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
    }
  } catch {
    return "";
  }

  return "";
}

async function fetchMediaPage(activeType: MediaType, page: number): Promise<{ items: MediaItem[]; totalPages: number }> {
  const endpoint = activeType === "fotos" ? "photos" : "videos";
  const cacheKey = `${endpoint}:${page}:${ITEMS_PER_PAGE}`;
  const cached = mediaPageCache.get(cacheKey);
  const isCacheValid = cached && Date.now() - cached.cachedAt <= MEDIA_CACHE_TTL_MS;

  let normalizedPayload: PaginatedMediaResponse | null = isCacheValid ? cached.payload : null;

  if (!normalizedPayload) {
    let inFlight = mediaPageInFlight.get(cacheKey);
    if (!inFlight) {
      inFlight = (async () => {
        const response = await fetch(`/api/media/${endpoint}?page=${page}&limit=${ITEMS_PER_PAGE}`);
        if (!response.ok) {
          return null;
        }

        const payload: PaginatedMediaResponse | any[] = await response.json();
        return normalizeMediaPayload(payload);
      })();
      mediaPageInFlight.set(cacheKey, inFlight);
    }

    normalizedPayload = await inFlight;
    mediaPageInFlight.delete(cacheKey);

    if (normalizedPayload) {
      mediaPageCache.set(cacheKey, {
        payload: normalizedPayload,
        cachedAt: Date.now(),
      });
    }
  }

  if (!normalizedPayload) {
    return { items: [], totalPages: 1 };
  }

  const normalizedItems = normalizedPayload.items.map((item: any) => ({
    id: String(item.id ?? ""),
    type: item.type === "videos" ? "videos" : "fotos",
    title: String(item.title ?? ""),
    description: String(item.description ?? ""),
    thumbnail: String(item.thumbnail ?? ""),
    href: String(item.link ?? item.href ?? "#"),
  })) as MediaItem[];

  return {
    items: normalizedItems,
    totalPages: normalizedPayload.totalPages > 0 ? normalizedPayload.totalPages : 1,
  };
}

export function MediaViewerPage() {
  const { type, id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locale } = useI18n();
  const content = getContentByLocale(locale);

  const activeType = validTypes.includes(type as MediaType) ? (type as MediaType) : null;
  const requestedId = decodeURIComponent(id ?? "");
  const initialPage = Math.max(1, Number(searchParams.get("page") ?? 1));

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [items, setItems] = useState<MediaItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage, type]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!activeType) {
        return;
      }

      const result = await fetchMediaPage(activeType, currentPage);
      if (cancelled) {
        return;
      }

      if (result.items.length > 0) {
        setItems(result.items);
        setTotalPages(result.totalPages);
        return;
      }

      const fallback = getMediaByLocale(locale)
        .filter((item) => item.type === activeType)
        .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

      setItems(fallback);
      setTotalPages(Math.max(1, Math.ceil(getMediaByLocale(locale).filter((item) => item.type === activeType).length / ITEMS_PER_PAGE)));
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [activeType, currentPage, locale]);

  const activeIndex = useMemo(() => items.findIndex((item) => item.id === requestedId), [items, requestedId]);
  const activeItem = activeIndex >= 0 ? items[activeIndex] : null;

  useEffect(() => {
    if (!activeType || !items.length) {
      return;
    }

    if (!activeItem) {
      navigate(`/midia/${activeType}/view/${encodeURIComponent(items[0].id)}?page=${currentPage}`, { replace: true });
    }
  }, [activeItem, activeType, currentPage, items, navigate]);

  const goToItem = async (direction: "prev" | "next") => {
    if (!activeType || activeIndex < 0) {
      return;
    }

    if (direction === "prev") {
      if (activeIndex > 0) {
        const target = items[activeIndex - 1];
        navigate(`/midia/${activeType}/view/${encodeURIComponent(target.id)}?page=${currentPage}`);
        return;
      }

      if (currentPage > 1) {
        const prevPage = currentPage - 1;
        const result = await fetchMediaPage(activeType, prevPage);
        const target = result.items[result.items.length - 1];
        if (target) {
          navigate(`/midia/${activeType}/view/${encodeURIComponent(target.id)}?page=${prevPage}`);
        }
      }

      return;
    }

    if (activeIndex < items.length - 1) {
      const target = items[activeIndex + 1];
      navigate(`/midia/${activeType}/view/${encodeURIComponent(target.id)}?page=${currentPage}`);
      return;
    }

    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      const result = await fetchMediaPage(activeType, nextPage);
      const target = result.items[0];
      if (target) {
        navigate(`/midia/${activeType}/view/${encodeURIComponent(target.id)}?page=${nextPage}`);
      }
    }
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && activeType) {
        navigate(`/midia/${activeType}?page=${currentPage}`);
      }

      if (event.key === "ArrowLeft") {
        goToItem("prev");
      }

      if (event.key === "ArrowRight") {
        goToItem("next");
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  if (!activeType || !activeItem) {
    return (
      <section className="section info-panel">
        <p className="info-copy">{locale === "pt" ? "Item não encontrado." : "Item not found."}</p>
        <Link className="hero-cta" to="/midia/fotos">
          {content.media.photosLabel}
        </Link>
      </section>
    );
  }

  const prevLabel = locale === "pt" ? "Anterior" : "Previous";
  const nextLabel = locale === "pt" ? "Próxima" : "Next";
  const backLabel = locale === "pt" ? "Voltar para galeria" : "Back to gallery";
  const closeLabel = locale === "pt" ? "Fechar" : "Close";
  const canGoPrev = activeIndex > 0 || currentPage > 1;
  const canGoNext = activeIndex < items.length - 1 || currentPage < totalPages;
  const embedUrl = activeItem.type === "videos" ? getYouTubeEmbedUrl(activeItem.href) : "";

  return (
    <>
      <PageSeo
        title={`${activeItem.title} | ${content.seo.defaultTitle}`}
        description={activeItem.description || content.media.subtitle}
      />

      <section className="media-viewer-page" aria-label={activeItem.title}>
        <header className="media-viewer-header">
          <Link className="media-viewer-back" to={`/midia/${activeType}?page=${currentPage}`}>
            {backLabel}
          </Link>
          <h1 className="media-viewer-title">{activeItem.title}</h1>
          <Link className="media-viewer-close" to={`/midia/${activeType}?page=${currentPage}`}>
            {closeLabel}
          </Link>
        </header>

        <div className="media-viewer-stage">
          {activeItem.type === "videos" && embedUrl ? (
            <iframe
              className="media-viewer-video"
              src={embedUrl}
              title={activeItem.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <img
              className="media-viewer-image"
              src={activeItem.href && activeItem.href !== "#" ? activeItem.href : activeItem.thumbnail}
              alt={activeItem.title}
              loading="eager"
              decoding="async"
              onError={(event) => {
                event.currentTarget.src = activeItem.thumbnail;
              }}
            />
          )}
        </div>

        <footer className="media-viewer-controls">
          <button type="button" className="media-viewer-btn" onClick={() => goToItem("prev")} disabled={!canGoPrev}>
            {prevLabel}
          </button>
          <span className="media-viewer-counter">{currentPage}/{totalPages}</span>
          <button type="button" className="media-viewer-btn" onClick={() => goToItem("next")} disabled={!canGoNext}>
            {nextLabel}
          </button>
        </footer>
      </section>
    </>
  );
}
